import { useTranslation } from "@sagot/i18n";
import { cn, Icon, type IconName } from "@sagot/ui";
import { STEP_ICONS } from "../builder/step-icons";
import type { DiffStatus, PathDiff, StepDiff } from "../p4-types";
import type { Step } from "../types";

const V = "engage.workflows.versions";

const STATUS_STYLE: Record<DiffStatus, { row: string; icon: IconName }> = {
  added: { row: "border-success bg-success-soft", icon: "plus" },
  removed: { row: "border-danger bg-danger-soft", icon: "minus" },
  changed: { row: "border-warning bg-warning-soft", icon: "pencil" },
  moved: { row: "border-primary bg-primary-soft", icon: "arrow-right" },
  unchanged: { row: "border-line bg-surface", icon: "check" },
};

const isDiffStatus = (value: string): value is DiffStatus => value in STATUS_STYLE;
const statusOf = (step: StepDiff): DiffStatus => (isDiffStatus(step.status) ? step.status : "unchanged");
const isStepType = (value: string): value is Step["type"] => value in STEP_ICONS;

export type Side = "base" | "target";

/** The steps of one side, grouped by the path they sit on (a step that was not on that side is left out). */
export function stepsBySide(paths: readonly PathDiff[], side: Side): Map<string, StepDiff[]> {
  const grouped = new Map<string, StepDiff[]>();
  for (const step of paths.flatMap((path) => path.steps)) {
    const index = side === "base" ? step.base_index : step.target_index;
    const path = side === "base" ? step.base_path : step.path;
    if (index === null || path === null) continue;
    grouped.set(path, [...(grouped.get(path) ?? []), step]);
  }
  const position = (step: StepDiff): number => (side === "base" ? step.base_index : step.target_index) ?? 0;
  for (const steps of grouped.values()) steps.sort((a, b) => position(a) - position(b));
  return grouped;
}

function StepLine({ step }: { step: StepDiff }) {
  const { t } = useTranslation();
  const status = statusOf(step);
  const style = STATUS_STYLE[status];
  const type = isStepType(step.type) ? step.type : null;
  return (
    <li className={cn("flex items-start gap-2 rounded-md border p-2 text-xs", style.row)} data-status={status} data-testid="diff-step">
      <Icon name={type ? STEP_ICONS[type] : "circle"} className="mt-0.5 size-4 shrink-0 text-primary-ink" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold">{t(`engage.workflows.steps.${step.type}`, { defaultValue: step.type })}</p>
        <p className="truncate font-mono text-[11px] text-fg-subtle">{step.name ?? step.step_id}</p>
        {status === "changed" && step.changed_fields.length > 0 ? <p className="mt-0.5 text-[11px] text-fg-muted">{t(`${V}.changedFields`, { fields: step.changed_fields.join(", ") })}</p> : null}
      </div>
      <span className={cn("inline-flex shrink-0 items-center gap-1", status === "unchanged" ? "text-fg-muted" : "font-bold")} data-testid="diff-status">
        <Icon name={style.icon} className="size-3" />
        {t(`${V}.status.${status}`)}
      </span>
    </li>
  );
}

interface CompareColumnProps {
  title: string;
  side: Side;
  paths: readonly PathDiff[];
}

/** One side of the comparison: every path with the steps it had on that side, changed steps marked with an icon and a word. */
export function CompareColumn({ title, side, paths }: CompareColumnProps) {
  const { t } = useTranslation();
  const grouped = stepsBySide(paths, side);
  const ids = [...new Set([...paths.map((path) => path.path), ...grouped.keys()])].sort();
  return (
    <section className="min-w-0 space-y-3" aria-label={title} data-testid={`compare-${side}`}>
      <h3 className="text-sm font-bold">{title}</h3>
      {ids.map((id) => {
        const steps = grouped.get(id) ?? [];
        return (
          <div key={id} className="rounded-lg border border-line bg-surface">
            <p className="border-b border-line px-3 py-2 text-xs font-bold">{t("engage.workflows.builder.path", { id })}</p>
            {steps.length === 0 ? (
              <p className="p-3 text-xs text-fg-subtle">{t(`${V}.noSteps`)}</p>
            ) : (
              <ol className="space-y-1.5 p-2">{steps.map((step) => <StepLine key={step.step_id} step={step} />)}</ol>
            )}
          </div>
        );
      })}
    </section>
  );
}
